import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { Pill } from "../components/Pill";

type TabId = "keys" | "providers" | "logs" | "settings";

type ApiKey = {
  id: string;
  label: string;
  prefix: string;
  packIds?: string[];
  allowedProviders?: string[];
  providerKeyId?: string | null;
  rpmLimit?: number | null;
  enabled: boolean;
  createdAt: number;
  revokedAt?: number;
  lastUsedAt?: number;
  rotatedTo?: string;
  rotatedFrom?: string;
};

type ProviderKey = {
  id: string;
  provider: "openai" | "anthropic" | "gemini";
  label: string;
  suffix: string;
  isDefault: boolean;
  createdAt: number;
  revokedAt?: number;
};

type Settings = {
  enabled: boolean;
  defaultRpmLimit: number;
  workspaceRpmLimit: number;
  logMode: "masked" | "full";
  strictMode: boolean;
  thresholdBoost?: number;
};

type Log = {
  id: string;
  apiKeyId: string;
  service?: string;
  provider: string;
  model: string;
  decision: "allowed" | "sanitised" | "blocked" | "warn";
  categories: string[];
  score: number;
  hardBlock: boolean;
  sanitised: boolean;
  maskedPreview: string;
  timings: { policyMs: number; rewriteMs: number; providerMs: number; totalMs: number };
  providerError?: string;
  createdAt: number;
};

const DEFAULT_SETTINGS: Settings = {
  enabled: false,
  defaultRpmLimit: 60,
  workspaceRpmLimit: 600,
  logMode: "masked",
  strictMode: false,
};

export function ApiAccessPage() {
  const [tab, setTab] = useState<TabId>("keys");
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [providerKeys, setProviderKeys] = useState<ProviderKey[]>([]);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const boot = await api.getGatewayBootstrap();
        setSettings({ ...DEFAULT_SETTINGS, ...(boot.gateway ?? {}) });
        setApiKeys((boot.apiKeys as ApiKey[]) ?? []);
        setProviderKeys((boot.providerKeys as ProviderKey[]) ?? []);
      } finally {
        setBootstrapping(false);
      }
    })();
  }, []);

  return (
    <>
      <header className="sg-page-header">
        <div>
          <h1 className="sg-page-title">API Access</h1>
          <div className="sg-page-sub">Control Plane · Provider Keys · Policy Assignment · Audit Logs</div>
        </div>
        <div className="sg-row" style={{ gap: 8 }}>
          <Pill kind={settings.enabled ? "allow" : "neutral"}>
            {settings.enabled ? "Gateway Live" : "Gateway Disabled"}
          </Pill>
        </div>
      </header>

      <div className="sg-tabs" style={{ marginBottom: 18, display: "flex", gap: 6 }}>
        <TabBtn active={tab === "keys"} onClick={() => setTab("keys")}>API Keys</TabBtn>
        <TabBtn active={tab === "providers"} onClick={() => setTab("providers")}>Providers</TabBtn>
        <TabBtn active={tab === "logs"} onClick={() => setTab("logs")}>Logs</TabBtn>
        <TabBtn active={tab === "settings"} onClick={() => setTab("settings")}>Settings</TabBtn>
      </div>

      {bootstrapping ? (
        <Skeleton />
      ) : tab === "keys" ? (
        <ApiKeysTab keys={apiKeys} onChange={setApiKeys} providerKeys={providerKeys} />
      ) : tab === "providers" ? (
        <ProvidersTab keys={providerKeys} onChange={setProviderKeys} />
      ) : tab === "logs" ? (
        <LogsTab apiKeys={apiKeys} />
      ) : (
        <SettingsTab settings={settings} onChange={setSettings} />
      )}
    </>
  );
}

/** Neutral loading skeleton shown while the bootstrap callable is in flight.
 *  Gives the admin an impression of structure instead of a bare "Loading…". */
function Skeleton() {
  return (
    <div className="sg-skeleton-stack" aria-hidden>
      <div className="sg-skeleton sg-skeleton-panel" style={{ height: 120 }} />
      <div className="sg-skeleton sg-skeleton-panel" style={{ height: 220, marginTop: 16 }} />
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      className={`sg-btn ${active ? "sg-btn-primary" : "sg-btn-ghost"} sg-btn-sm`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ─── API keys ───────────────────────────────────────────────────────────────

function ApiKeysTab({
  keys,
  onChange,
  providerKeys,
}: {
  keys: ApiKey[];
  onChange: (k: ApiKey[]) => void;
  providerKeys: ProviderKey[];
}) {
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [packIds, setPackIds] = useState<string[]>(["general"]);
  const [allowedProviders, setAllowedProviders] = useState<string[]>([]);
  const [rpmLimit, setRpmLimit] = useState<string>("");
  const [providerKeyId, setProviderKeyId] = useState<string>("");
  const [freshKey, setFreshKey] = useState<{ plaintext: string; label: string } | null>(null);
  const [availablePacks, setAvailablePacks] = useState<Array<{ id: string; name: string }>>([
    { id: "general", name: "General" },
  ]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listPolicyPacks();
        setAvailablePacks(r.packs.map((p) => ({ id: p.id, name: p.name })));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const configuredProviders = useMemo(
    () => Array.from(new Set(providerKeys.filter((k) => !k.revokedAt).map((k) => k.provider))),
    [providerKeys],
  );

  async function handleCreate() {
    if (!label.trim()) return;
    setCreating(true);
    try {
      const r = await api.createGatewayApiKey({
        label: label.trim(),
        packIds,
        allowedProviders: allowedProviders.length ? allowedProviders : undefined,
        rpmLimit: rpmLimit ? Number(rpmLimit) : null,
        providerKeyId: providerKeyId || null,
      });
      onChange([r.key, ...keys]);
      setFreshKey({ plaintext: r.plaintext, label: r.key.label });
      setLabel("");
      setPackIds(["general"]);
      setAllowedProviders([]);
      setRpmLimit("");
      setProviderKeyId("");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm("Revoke this API key? In-flight requests using it will start failing immediately.")) return;
    await api.revokeGatewayApiKey({ id });
    onChange(keys.map((k) => (k.id === id ? { ...k, enabled: false, revokedAt: Date.now() } : k)));
  }

  async function handleRotate(id: string) {
    if (!confirm("Rotate this API key?\n\nA new key is generated and shown once.\nThe old key keeps working for 24 h so you can redeploy without downtime.")) return;
    const r = await api.rotateGatewayApiKey({ id });
    // Update the list: old key gets revokedAt in the future; add new key at the top.
    onChange([r.key as ApiKey, ...keys.map((k) => (k.id === id ? { ...k, rotatedTo: r.key.id, revokedAt: r.graceUntil } : k))]);
    setFreshKey({ plaintext: r.plaintext, label: r.key.label });
  }

  return (
    <>
      {freshKey && (
        <div className="sg-panel" style={{ marginBottom: 18, borderColor: "var(--sg-ok, #36d399)" }}>
          <div className="sg-panel-pad">
            <div className="sg-section-label">New Key: {freshKey.label}</div>
            <div className="sg-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
              This is the only time the plaintext will be shown. Copy it now — you cannot retrieve it later.
            </div>
            <code style={{ display: "block", padding: 10, background: "#111", color: "#e5e7eb", borderRadius: 6, wordBreak: "break-all", fontSize: 13 }}>
              {freshKey.plaintext}
            </code>
            <div style={{ marginTop: 10 }}>
              <button className="sg-btn sg-btn-sm" onClick={() => navigator.clipboard.writeText(freshKey.plaintext)}>Copy</button>
              <button className="sg-btn sg-btn-ghost sg-btn-sm" style={{ marginLeft: 8 }} onClick={() => setFreshKey(null)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-section-label">Create a Key</div>
          <div className="sg-row" style={{ gap: 10, flexWrap: "wrap" }}>
            <input
              className="sg-input"
              placeholder="Label (e.g. legal-assistant-prod)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ flex: "2 1 240px" }}
            />
            <input
              className="sg-input"
              placeholder="RPM limit (optional)"
              value={rpmLimit}
              onChange={(e) => setRpmLimit(e.target.value.replace(/[^0-9]/g, ""))}
              style={{ width: 160 }}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>Policy packs</div>
            <div className="sg-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {availablePacks.map((p) => (
                <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={packIds.includes(p.id)}
                    disabled={p.id === "general"}
                    onChange={(e) => {
                      if (e.target.checked) setPackIds([...packIds, p.id]);
                      else setPackIds(packIds.filter((x) => x !== p.id));
                    }}
                  />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>Allowed providers (empty = all configured)</div>
            <div className="sg-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {(["openai", "anthropic", "gemini"] as const).map((p) => (
                <label key={p} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={allowedProviders.includes(p)}
                    onChange={(e) => {
                      if (e.target.checked) setAllowedProviders([...allowedProviders, p]);
                      else setAllowedProviders(allowedProviders.filter((x) => x !== p));
                    }}
                  />
                  {p}
                  {!configuredProviders.includes(p) && <span className="sg-muted" style={{ fontSize: 11 }}>(not configured)</span>}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>Bind to provider key (optional)</div>
            <select
              className="sg-input"
              value={providerKeyId}
              onChange={(e) => setProviderKeyId(e.target.value)}
              style={{ maxWidth: 360 }}
            >
              <option value="">Use workspace default for each provider</option>
              {providerKeys
                .filter((k) => !k.revokedAt)
                .map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.provider} · {k.label} (…{k.suffix})
                  </option>
                ))}
            </select>
            <div className="sg-muted" style={{ fontSize: 11.5, marginTop: 6 }}>
              When set, requests on this API key always use this provider key. Requests can still override per-call via <code>metadata.provider_key_id</code> or <code>X-Ventrin-Provider-Key</code>.
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={handleCreate} disabled={!label.trim() || creating}>
              {creating ? "Creating…" : "Generate Key"}
            </button>
          </div>
        </div>
      </div>

      <div className="sg-section-label">Active Keys ({keys.filter((k) => k.enabled).length})</div>
      {keys.length === 0 ? (
        <div className="sg-empty">No API keys yet.</div>
      ) : (
        <div className="sg-panel">
          <table className="sg-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Prefix</th>
                <th>Packs</th>
                <th>Providers</th>
                <th>Bound key</th>
                <th>RPM</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const bound = k.providerKeyId ? providerKeys.find((p) => p.id === k.providerKeyId) : undefined;
                const now = Date.now();
                const inGrace = !!(k.rotatedTo && k.revokedAt && k.revokedAt > now);
                const hoursLeft = inGrace && k.revokedAt ? Math.max(1, Math.round((k.revokedAt - now) / 3_600_000)) : 0;
                const fullyRevoked = !k.enabled || (k.revokedAt && k.revokedAt <= now);
                return (
                  <tr key={k.id}>
                    <td>{k.label}</td>
                    <td><code style={{ fontSize: 12 }}>{k.prefix}…</code></td>
                    <td>{k.packIds?.length ? k.packIds.join(", ") : "workspace default"}</td>
                    <td>{k.allowedProviders?.length ? k.allowedProviders.join(", ") : "all"}</td>
                    <td>{bound ? `${bound.provider} · …${bound.suffix}` : "default"}</td>
                    <td>{k.rpmLimit ?? "default"}</td>
                    <td>
                      {inGrace ? (
                        <Pill kind="warn" title={`Rotated — this key stops working in ~${hoursLeft}h`}>
                          rotating · {hoursLeft}h
                        </Pill>
                      ) : (
                        <Pill kind={fullyRevoked ? "neutral" : "allow"}>
                          {fullyRevoked ? "revoked" : "active"}
                        </Pill>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {!fullyRevoked && !inGrace && (
                        <>
                          <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={() => handleRotate(k.id)}>Rotate</button>
                          <button className="sg-btn sg-btn-ghost sg-btn-sm" style={{ marginLeft: 6 }} onClick={() => handleRevoke(k.id)}>Revoke</button>
                        </>
                      )}
                      {inGrace && (
                        <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={() => handleRevoke(k.id)} title="Revoke now instead of waiting for grace to elapse">Revoke now</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Providers ─────────────────────────────────────────────────────────────

function ProvidersTab({
  keys,
  onChange,
}: {
  keys: ProviderKey[];
  onChange: (k: ProviderKey[]) => void;
}) {
  const [provider, setProvider] = useState<"openai" | "anthropic" | "gemini">("openai");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  /** Best-effort shape-check: does the pasted key look like one from the
   *  selected provider? Purely a UX nudge — the server does the real check
   *  via the provider on first use. */
  const shapeWarning: string | null = useMemo(() => {
    const v = value.trim();
    if (!v) return null;
    if (v.length < 16) return "That looks too short for a provider key.";
    if (provider === "openai" && !/^sk-/.test(v)) {
      return "OpenAI keys usually start with 'sk-'. Double-check the provider.";
    }
    if (provider === "anthropic" && !/^sk-ant-/.test(v)) {
      return "Anthropic keys usually start with 'sk-ant-'. Double-check the provider.";
    }
    if (provider === "gemini" && !/^AIza/.test(v)) {
      return "Gemini keys usually start with 'AIza'. Double-check the provider.";
    }
    return null;
  }, [provider, value]);

  async function handleSave() {
    if (!value.trim() || !label.trim()) return;
    setSaving(true);
    try {
      const r = await api.upsertGatewayProviderKey({ provider, label: label.trim(), value: value.trim(), isDefault });
      onChange([r.key, ...keys.filter((k) => !(isDefault && k.provider === provider) || k.id === r.key.id)]);
      setValue("");
      setLabel("");
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm("Revoke this provider key? Any traffic routed to it will start failing.")) return;
    await api.revokeGatewayProviderKey({ id });
    onChange(keys.map((k) => (k.id === id ? { ...k, revokedAt: Date.now(), isDefault: false } : k)));
  }

  return (
    <>
      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-section-label">Add a Provider Key</div>
          <div className="sg-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
            Keys are encrypted with AES-256-GCM before storage and never leave the server in plaintext. The default key for each provider is used when a gateway request maps to that provider.
          </div>
          <div className="sg-row" style={{ gap: 10, flexWrap: "wrap" }}>
            <select className="sg-input" value={provider} onChange={(e) => setProvider(e.target.value as any)}>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="gemini">Gemini</option>
            </select>
            <input
              className="sg-input"
              placeholder="Label (e.g. prod)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ flex: "1 1 180px" }}
            />
            <input
              className="sg-input"
              type="password"
              placeholder="Paste the provider API key"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: "2 1 280px" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Default for this provider
            </label>
            <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={handleSave} disabled={saving || !value.trim() || !label.trim()}>
              {saving ? "Saving…" : "Save Key"}
            </button>
          </div>
          {shapeWarning && (
            <div className="sg-muted" style={{ fontSize: 12, marginTop: 10, color: "var(--sg-amber, #a86e1a)" }}>
              {shapeWarning}
            </div>
          )}
        </div>
      </div>

      <div className="sg-section-label">Configured Provider Keys</div>
      {keys.length === 0 ? (
        <div className="sg-empty">No provider keys yet. Add one above before enabling the gateway.</div>
      ) : (
        <div className="sg-panel">
          <table className="sg-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Label</th>
                <th>Suffix</th>
                <th>Default</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.provider}</td>
                  <td>{k.label}</td>
                  <td><code style={{ fontSize: 12 }}>…{k.suffix}</code></td>
                  <td>{k.isDefault ? "yes" : ""}</td>
                  <td>
                    <Pill kind={k.revokedAt ? "neutral" : "allow"}>
                      {k.revokedAt ? "revoked" : "active"}
                    </Pill>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {!k.revokedAt && (
                      <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={() => handleRevoke(k.id)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Logs ──────────────────────────────────────────────────────────────────

function LogsTab({ apiKeys }: { apiKeys: ApiKey[] }) {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterDecision, setFilterDecision] = useState<string>("");
  const [filterApiKeyId, setFilterApiKeyId] = useState<string>("");
  const [filterProvider, setFilterProvider] = useState<string>("");

  async function load() {
    setLoading(true);
    try {
      const filter: any = {};
      if (filterDecision) filter.decision = filterDecision;
      if (filterApiKeyId) filter.apiKeyId = filterApiKeyId;
      if (filterProvider) filter.provider = filterProvider;
      const r = await api.listGatewayLogs({ limit: 100, filter });
      setLogs((r.logs as Log[]) ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select className="sg-input" value={filterDecision} onChange={(e) => setFilterDecision(e.target.value)}>
              <option value="">All decisions</option>
              <option value="allowed">Allowed</option>
              <option value="sanitised">Sanitised</option>
              <option value="warn">Warn</option>
              <option value="blocked">Blocked</option>
            </select>
            <select className="sg-input" value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}>
              <option value="">All providers</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="gemini">Gemini</option>
            </select>
            <select className="sg-input" value={filterApiKeyId} onChange={(e) => setFilterApiKeyId(e.target.value)}>
              <option value="">All API keys</option>
              {apiKeys.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
            <button className="sg-btn sg-btn-sm" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="sg-empty sg-empty-cta">
          <strong>No gateway activity yet.</strong>
          <span style={{ display: "block", marginTop: 6, color: "var(--sg-ink-3)" }}>
            Generate an API key in the API Keys tab and POST to{" "}
            <code>https://www.ventrin.com/v1/chat/completions</code>. Every request you forward through the gateway shows up here.
          </span>
          <div className="sg-row" style={{ gap: 8, marginTop: 14, justifyContent: "center" }}>
            <a className="sg-btn sg-btn-primary sg-btn-sm" href="https://www.ventrin.com/api-guide.html" target="_blank" rel="noopener">Read the API guide</a>
          </div>
        </div>
      ) : (
        <div className="sg-panel">
          <table className="sg-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Decision</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Service</th>
                <th>Categories</th>
                <th>Score</th>
                <th>Latency</th>
                <th>Preview</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>
                    <Pill kind={decisionKind(log.decision)}>
                      {log.decision}{log.hardBlock ? " · hard" : ""}
                    </Pill>
                  </td>
                  <td>{log.provider}</td>
                  <td>{log.model}</td>
                  <td>{log.service ?? "—"}</td>
                  <td>{log.categories.slice(0, 3).join(", ") || "—"}</td>
                  <td>{Math.round(log.score)}</td>
                  <td>{log.timings.totalMs}ms</td>
                  <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {log.maskedPreview}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function decisionKind(decision: string): "allow" | "warn" | "rewrite" | "block" | "neutral" {
  switch (decision) {
    case "allowed": return "allow";
    case "sanitised": return "rewrite";
    case "warn": return "warn";
    case "blocked": return "block";
    default: return "neutral";
  }
}

// ─── Settings ──────────────────────────────────────────────────────────────

function SettingsTab({ settings, onChange }: { settings: Settings; onChange: (s: Settings) => void }) {
  const [saving, setSaving] = useState<string | null>(null);
  const [draft, setDraft] = useState(settings);

  useEffect(() => { setDraft(settings); }, [settings]);

  async function save(patch: Partial<Settings>) {
    setSaving(Object.keys(patch)[0] ?? "save");
    try {
      const r = await api.updateGatewaySettings(patch);
      onChange(r.gateway as Settings);
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-section-label">Gateway Status</div>
          <div className="sg-row" style={{ gap: 10, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => save({ enabled: e.target.checked })}
                disabled={saving === "enabled"}
              />
              Accept traffic on <code>POST /v1/chat/completions</code>
            </label>
          </div>
          <div className="sg-muted" style={{ fontSize: 12, marginTop: 8 }}>
            When disabled, the gateway returns 403 for every request regardless of API key.
          </div>
        </div>
      </div>

      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-section-label">Rate Limits</div>
          <div className="sg-row" style={{ gap: 16, flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              <span>Per-key default (rpm)</span>
              <input
                className="sg-input"
                style={{ width: 120 }}
                value={draft.defaultRpmLimit}
                onChange={(e) => setDraft({ ...draft, defaultRpmLimit: Number(e.target.value) || 0 })}
                onBlur={() => save({ defaultRpmLimit: draft.defaultRpmLimit })}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              <span>Workspace total (rpm)</span>
              <input
                className="sg-input"
                style={{ width: 120 }}
                value={draft.workspaceRpmLimit}
                onChange={(e) => setDraft({ ...draft, workspaceRpmLimit: Number(e.target.value) || 0 })}
                onBlur={() => save({ workspaceRpmLimit: draft.workspaceRpmLimit })}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="sg-panel" style={{ marginBottom: 18 }}>
        <div className="sg-panel-pad">
          <div className="sg-section-label">Enforcement Posture</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={draft.strictMode}
              onChange={(e) => save({ strictMode: e.target.checked })}
              disabled={saving === "strictMode"}
            />
            Strict mode — warnings auto-escalate to blocks
          </label>
          <div className="sg-muted" style={{ fontSize: 12 }}>
            Use for locked-down environments (e.g. regulated internal tools). In default mode, low-confidence signals are passed through with a warning on the response.
          </div>
        </div>
      </div>

      <div className="sg-panel">
        <div className="sg-panel-pad">
          <div className="sg-section-label">Logging</div>
          <div className="sg-row" style={{ gap: 16, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="logMode"
                checked={draft.logMode === "masked"}
                onChange={() => save({ logMode: "masked" })}
              />
              Masked preview only (recommended)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="logMode"
                checked={draft.logMode === "full"}
                onChange={() => save({ logMode: "full" })}
              />
              Full encrypted body (opt-in)
            </label>
          </div>
          <div className="sg-muted" style={{ fontSize: 12, marginTop: 10 }}>
            Full-body logs are encrypted with AES-256-GCM on the server. Only workspace admins can reveal them, and every reveal is audited.
          </div>
        </div>
      </div>
    </>
  );
}
